import React from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Info } from "@mui/icons-material";
import { skipToken } from "@reduxjs/toolkit/query";

import { selectCurrentAccount, selectCurrentSubscription, useGetAccountSubscriptionsQuery, useGetAccountSubscriptionUsageQuery } from "fond/api";
import { SubscriptionUsageType } from "fond/api/accountSlice";
import { useFeatureFlag } from "fond/featureFlags";
import { Navigation } from "fond/settings";
import { Store, Subscription } from "fond/types";
import { getDesignQuota, getPlannerQuota } from "fond/utils/subscription";
import { NonIdealState, PageContent } from "fond/widgets";

import InsightsPage from "./InsightsPage/InsightsPage";
import SubscriptionPeriod from "./SubscriptionPeriod/SubscriptionPeriod";

/**
 * A top level component used for displaying Insights Settings.
 */
const Insights: React.FC = () => {
  const account = useSelector(selectCurrentAccount);
  useGetAccountSubscriptionsQuery(account?.ID ?? skipToken, { refetchOnMountOrArgChange: true });
  const subscription = useSelector((state: Store): Subscription | null => (account?.ID && selectCurrentSubscription(state, account.ID)) || null);
  const { value: displayDesignPremises } = useFeatureFlag("display_design_premises", false);
  // The selected type is kept in the URL so that other pages can link directly to it, e.g. `/settings/insights?type=design`.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUsageType: SubscriptionUsageType = searchParams.get("type") === "design" ? "design" : "planner";

  const { hasPlanner } = getPlannerQuota(subscription);
  const hasDesign = displayDesignPremises && getDesignQuota(subscription).hasDesign;
  // Planner is the default, design usage is only shown when the account has a design quota.
  const usageType: SubscriptionUsageType = hasDesign && (selectedUsageType === "design" || !hasPlanner) ? "design" : "planner";
  const canViewUsage = usageType === "planner" ? hasPlanner : hasDesign;

  // Use `currentData` so the previous type's usage is not shown while the newly selected type is loading.
  const { currentData: subscriptionUsage, isFetching } = useGetAccountSubscriptionUsageQuery(
    subscription && canViewUsage ? { subscriptionId: subscription.ID, type: usageType } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  if (account && (subscriptionUsage || isFetching)) {
    return (
      <PageContent
        title="Insights"
        subTitle="DASHBOARD"
        action={subscriptionUsage && <SubscriptionPeriod subscriptionUsage={subscriptionUsage} />}
        mainContent={
          <InsightsPage
            subscriptionUsage={subscriptionUsage}
            usageType={usageType}
            showUsageTypeTabs={hasPlanner && hasDesign}
            onUsageTypeChange={(type) => setSearchParams({ type }, { replace: true })}
          />
        }
        navigation={<Navigation />}
      />
    );
  }

  return (
    <PageContent
      testId="insights-page-not-available"
      title="Insights"
      mainContent={<NonIdealState title="Oops" description="This page is currently not available." icon={<Info />} />}
      navigation={<Navigation />}
    />
  );
};

export default Insights;
